// Trusted origins from which file downloads are permitted.
// URLs not matching one of these hostnames (or the local /assets path) will be rejected
// to prevent Server-Side Request Forgery (SSRF) attacks.
const TRUSTED_DOWNLOAD_ORIGINS: ReadonlySet<string> = new Set([
  'quantified.ai',              // all *.quantified.ai subdomains (API + CDN)
  'amazonaws.com',              // AWS S3 / CloudFront content buckets
]);

/**
 * Returns true when the hostname of `url` matches a trusted origin suffix,
 * or when `url` is an absolute path served by the same origin (e.g. /assets/…).
 */
export function isTrustedDownloadUrl(url: string): boolean {
  // Allow relative paths — they are served from the same origin.
  if (url.startsWith('/')) return true;

  try {
    const { hostname } = new URL(url);
    // Accept exact matches and subdomain matches (e.g. foo.amazonaws.com).
    return [...TRUSTED_DOWNLOAD_ORIGINS].some(
      trusted => hostname === trusted || hostname.endsWith(`.${trusted}`)
    );
  } catch {
    // Unparseable URLs are not trusted.
    return false;
  }
}

// Fetches a file from a cross-origin URL (e.g. S3) and triggers a browser download.
// Throws if the URL does not belong to a trusted origin, guarding against SSRF.
export async function downloadFileFromUrl(url: string, fallbackFilename = 'document'): Promise<void> {
  if (!isTrustedDownloadUrl(url)) {
    throw new Error(`Download blocked: URL origin is not in the trusted allowlist (${url})`);
  }

  const filename = url.split('/').pop()?.split('?')[0] || fallbackFilename;
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  const blob = await res.blob();
  const anchor = document.createElement('a');
  anchor.href = URL.createObjectURL(blob);
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(anchor.href);
}
